import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiContentDrawer,
  MiDetailFields,
  MiFileList,
  MiFileUpload,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  ISSUE_STATUSES,
  VALID_IMAGE_FILE_MIME_TYPES,
} from "@miplatform/constants";
import { mdiThumbDown, mdiThumbUp } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { useIssueRepairDrawerViewModel } from "./useIssueRepairDrawerViewModel";

const CLOSEOUT_TYPES = {
  ISSUE: "Issue",
  REPAIR: "Repair",
};

export function IssueRepairDrawer({
  open = false,
  task = {},
  issue = {},
  onClose = () => null,
}) {
  const {
    control,
    isUpdating,
    isIssueResolved,
    submitDisabled,
    isAlreadyResolved,
    setIsAlreadyResolved,
    onClickYes,
    onClickNo,
    setValue,
    onSubmit,
    handleClose,
    setIssueImages,
    setRepairImages,
    deleteImage,
    getIssueTypes,
    issueTypes,
  } = useIssueRepairDrawerViewModel({
    task,
    issue,
    onClose,
  });

  const theme = useTheme();
  const { classes } = useStyles();

  const [uploadedIssueImages, setUploadedIssueImages] = useState([]);
  const [uploadedRepairImages, setUploadedRepairImages] = useState([]);

  useEffect(() => {
    getIssueTypes();
  }, []);

  useEffect(() => {
    if (open && isUpdating) {
      setValue("description", issue.description);
      setValue("issueImages", issue.issueImages);
      setValue("issueTypeId", issue.issueTypeId);
      setUploadedIssueImages(issue.issueImages);

      if (issue.status === ISSUE_STATUSES.RESOLVED) {
        onClickYes();
        setIsAlreadyResolved(true);

        setValue("repairDescription", issue.repairDescription);
        setUploadedRepairImages(issue.repairImages);
      }
    }
  }, [open, isUpdating]);

  const drawerActions = [
    {
      name: "Cancel",
      variant: BUTTON_VARIANTS.TEXT,
      color: BUTTON_COLORS.BLACK,
      onClick: handleClose,
    },
    {
      name: "Submit",
      variant: BUTTON_VARIANTS.CONTAINED,
      onClick: onSubmit(isUpdating),
      disabled: submitDisabled,
    },
  ];

  const issueFields = [
    {
      label: "Task Details",
      name: "description",
      formControl: control,
      setValue: (e) => {
        setValue("description", e.target.value, { shouldValidate: true });
      },
      multiline: true,
      rows: 4,
      InputProps: {
        inputProps: {
          className: classes.textArea,
          maxLength: 2500,
          "data-testid": "issue-description",
        },
      },
      width: "100%",
      required: true,
    },
    {
      label: "Issue Type",
      name: "issueTypeId",
      width: "100%",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      options: issueTypes,
    },
  ];

  const repairFields = [
    {
      label: "Repair Details",
      name: "repairDescription",
      formControl: control,
      setValue: (e) => {
        setValue("repairDescription", e.target.value, { shouldValidate: true });
      },
      multiline: true,
      rows: 4,
      InputProps: {
        inputProps: {
          className: classes.textArea,
          maxLength: 2500,
          "data-testid": "repair-description",
        },
      },
      width: "100%",
      disabled: !isIssueResolved,
    },
  ];

  const issueRepairCard = (closeoutType, onFileChange) => {
    const title = `${closeoutType} Details`;
    const existingFiles =
      closeoutType === CLOSEOUT_TYPES.ISSUE
        ? uploadedIssueImages
        : uploadedRepairImages;

    const backgroundColor =
      closeoutType === CLOSEOUT_TYPES.ISSUE
        ? theme.palette.red.light
        : theme.palette.grey.light;

    const opacity =
      closeoutType === CLOSEOUT_TYPES.REPAIR && !isIssueResolved ? 0.5 : 1;

    return (
      <Box
        className={classes.issueRepairContainer}
        sx={{ backgroundColor, opacity }}
        key={closeoutType}
      >
        <Box className={classes.header}>{title}</Box>
        <MiDetailFields
          detailFields={
            closeoutType === CLOSEOUT_TYPES.ISSUE ? issueFields : repairFields
          }
          containerStyle={classes.containerStyle}
        />
        {existingFiles.length > 0 && (
          <Box className={classes.existingFilesList}>
            Previously uploaded images
            <MiFileList
              files={existingFiles}
              onDelete={(file) => {
                deleteImage({ file, modifiedOn: issue.modifiedOn });
              }}
            />
          </Box>
        )}
        {open && (
          <MiFileUpload
            dragAndDrop
            onChange={onFileChange}
            acceptableFormatText="Acceptable formats: png, jpg, gif"
            validFileTypes={VALID_IMAGE_FILE_MIME_TYPES}
          />
        )}
        {closeoutType === CLOSEOUT_TYPES.ISSUE ? (
          <Box className={classes.issueRepairActions}>
            <Typography>Was this resolved?</Typography>
            <Box className={classes.buttons}>
              <MiButton
                title="Yes"
                color={isIssueResolved ? "success" : BUTTON_COLORS.BLACK}
                className={classes.btnYes}
                startIcon={{
                  path: mdiThumbUp,
                  iconSize: 0.8,
                  iconColor: isIssueResolved
                    ? theme.palette.common.white
                    : theme.palette.success.main,
                }}
                onClick={onClickYes}
                variant={isIssueResolved ? "contained" : "outlined"}
                data-testid={`${closeoutType}-repaired-btn`}
              />
              <MiButton
                title="No"
                color={
                  !isIssueResolved ? BUTTON_COLORS.RED : BUTTON_COLORS.BLACK
                }
                className={classes.btnNo}
                startIcon={{
                  path: mdiThumbDown,
                  iconSize: 0.8,
                  iconColor: isIssueResolved
                    ? theme.palette.error.main
                    : theme.palette.common.white,
                }}
                onClick={onClickNo}
                disabled={isAlreadyResolved}
                variant={!isIssueResolved ? "contained" : "outlined"}
                data-testid={`${closeoutType}-not-repaired-btn`}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <MiContentDrawer
      open={open}
      onClose={handleClose}
      title={
        isUpdating
          ? `Update Issue - ${issue.description}`
          : `New Issue - ${task.workTaskTypeName}`
      }
      actions={drawerActions}
    >
      <Box className={classes.issueRepairCardContainer}>
        {issueRepairCard(CLOSEOUT_TYPES.ISSUE, setIssueImages)}
        {isIssueResolved &&
          issueRepairCard(CLOSEOUT_TYPES.REPAIR, setRepairImages)}
      </Box>
    </MiContentDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  issueRepairCardContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: theme.spacing(0, 4),
    gap: theme.spacing(4),
  },
  issueRepairContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "column",
    padding: "12px 16px 16px",
    borderRadius: 10,
    justifyContent: "space-between",
  },
  existingFilesList: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    borderRadius: 4,
    margin: theme.spacing(0, 2, 2),
  },
  issueRepairActions: {
    display: "flex",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 16px",
    height: 50,
    borderRadius: 10,
    backgroundColor: theme.palette.common.white,
  },
  header: {
    fontSize: theme.fontSize.xlarge,
    fontWeight: theme.fontWeight.semiBold,
    marginBottom: 4,
  },
  textArea: {
    resize: "vertical",
  },
  fields: {
    padding: 10,
    backgroundColor: theme.palette.common.white,
    borderRadius: 8,
  },
  buttons: {
    display: "flex",
    gap: 8,
  },
  btnYes: {
    fontWeight: theme.fontWeight.bold,
  },
  btnNo: {
    fontWeight: theme.fontWeight.bold,
    ":disabled": {
      opacity: 0.5,
    },
  },
}));
